
import { Component } from 'nuxt-property-decorator'
import { FSXABaseComponent, FSXAGetters } from 'fsxa-pattern-library'
import { RichTextElement } from 'fsxa-api'
import BaseGridLayout from '../layouts/BaseGridLayout.vue'
import MessageBar from '../MessageBar.vue'
import LandingPageHeader from '../LandingPageHeader.vue'
import BaseButton from '../base/BaseButton.vue'
import TheHeader from '../header/TheHeader.vue'
import IHeaderNavElement from '../../shared/general/interfaces/IHeaderNavElement'
import IOption from '../../shared/general/interfaces/IOption'
import { caasLocale } from '../../shared/general/services/LanguageService'
import { Logger } from '../../shared/general/logger/Logger'
import { nuxt } from '../../shared/general/logger/LogKey'
import { globalLabelAsString } from '../../shared/general/services/StoreService'
import EButtonVariant from '../../shared/general/enums/EButtonVariant'
import { IIdMap, ILanguageRoute, INavDatStructEl } from '../../shared/fsxa/interfaces/IIdMap'
import BaseLink from '../base/BaseLink.vue'
import TheCookieBanner from '../TheCookieBanner.vue'

@Component({
  name: 'PageHeader',
  components: {
    TheCookieBanner,
    BaseLink,
    TheHeader,
    BaseButton,
    LandingPageHeader,
    MessageBar,
    BaseGridLayout,
  },
})
export default class PageHeader extends FSXABaseComponent {
  private maintenanceBarButtonVariant : EButtonVariant = EButtonVariant.NeutralOutline

  private get headerNavElements () : IHeaderNavElement[] {
    const getChildren = (item : INavDatStructEl, level : number) : IHeaderNavElement[] => item.children?.map((child) => {
      const headerNavElement : IHeaderNavElement = {
        key: this.idMap[child.id]?.caasDocumentId,
        path: this.getUrlByPageId(child.id) || '#',
        label: this.idMap[child.id]?.label,
      }

      // Cut off everything after level 4
      // I know, 2 is a strange number for this, but it works.
      // Source: Dude, trust me (it's because we start way later with counting, as level 1 and 2 are already there)
      if (child.children && child.children.length > 0 && level < 2) {
        headerNavElement.children = getChildren(child, level + 1)
      }

      return headerNavElement
    }) || []

    return this.structure.map<IHeaderNavElement>((item : INavDatStructEl) => ({
      key: this.idMap[item.id]?.caasDocumentId,
      label: this.idMap[item.id]?.label,
      path: this.getUrlByPageId(item.id) || '#',
      children: getChildren(item, 0),
    })) || []
  }

  private get idMap () : IIdMap {
    return this.$store.getters[FSXAGetters.navigationData]?.idMap || {}
  }

  private get structure () : INavDatStructEl[] {
    return this.$store.getters[FSXAGetters.navigationData]?.structure || []
  }

  private get searchPageUrl () : string {
    return this.getUrlByPageId(this.globalSettings?.data?.ps_country_project_settings?.pt_search_page?.referenceId || '') || ''
  }

  private get headerClasses () : string {
    return this.$store.state.ModalContent.isFilterOpen
      ? '!translate-y-[-93px] md:!translate-y-[0] z-50'
      : 'block'
  }

  private async selectLanguage (language : IOption) : Promise<void> {
    const errorPage = `${window.location.protocol}//${window.location.host}/error-page`
    const locale = language.value as string

    try {
      const languagePagesRaw = await fetch('/api/route-for-languages', {
        method: 'POST',
        body: JSON.stringify({
          id: this.getCurrentPageId(this.currentPage?.contentReference),
          datasetId: this.currentDataset?.id ?? '',
          localeList: this.languages.map((lang) => caasLocale(lang.value as string)),
        }),
      })

      if (languagePagesRaw.status !== 200) {
        Logger.error(nuxt, 'Could not load page in other languages. Redirect to /error-page', languagePagesRaw.text())
        window.location.href = errorPage
        return
      }

      const languagePages : ILanguageRoute[] = await languagePagesRaw.json()
      const route = languagePages.find((page : ILanguageRoute) => page.locale === caasLocale(locale))
      if (route) {
        window.location.href = `${window.location.protocol}//${window.location.host}${route.seoRoute}`
        return
      }

      window.location.href = errorPage
    } catch (e) {
      Logger.error(nuxt, 'An unknown error occurred. Redirect to /error-page', e)
      window.location.href = errorPage
    }
  }

  private getCurrentPageId (contentReference ?: string | null | undefined) {
    if (!contentReference) return ''
    if (contentReference.lastIndexOf('/') === -1) return ''
    if (contentReference.lastIndexOf('.') === -1) return ''
    return contentReference.slice(contentReference.lastIndexOf('/') + 1, contentReference.lastIndexOf('.'))
  }

  private get maintenanceBarText () : string | RichTextElement[] {
    return this.$store.state.Page.maintenanceBarText
  }

  private get maintenanceBarVisible () : boolean {
    return this.$store.state.Page.maintenanceBarVisible
  }

  private get maintenanceBarButtonLabel () : string {
    return globalLabelAsString('okay_label')
  }

  private maintenanceBarButtonClick () : void {
    this.$store.commit('Page/resetMaintenanceBar')
    sessionStorage.setItem('show-maintenance-bar', 'false')
  }

  private setDarkLayerVisible (value : boolean) : void {
    this.$store.commit('PageHeader/setIsDarkLayerVisible', value)
  }

  private get languages () : IOption[] {
    return this.$store.state.Locales.headerLocales
  }

  private get skipToMainContentLabel () : string {
    return globalLabelAsString('skip_to_main_content_label')
  }

  private get hasSlotCustomHeader () : boolean {
    return !!this.$slots.customHeader
  }
}
